import { StrictMode, Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './styles/app.css';
import { AppShell } from './app/AppShell';
import { Home } from './features/home/Home';
import { SurahIndex } from './features/quran/SurahIndex';
import { Reader } from './features/quran/Reader';
import { ListenHome, ReciterPage } from './features/player/Listen';
import { PrayerPage } from './features/prayer/Prayer';
import { PrayerStatsPage } from './features/prayer/PrayerStats';
import { FastingPage } from './features/fasting/Fasting';
import { FocusReader, KhatmahList } from './features/khatmah/KhatmahPages';
import { HifzGuide, HifzHome, HifzLesson, HifzReview } from './features/hifz/HifzPages';
import { DesignPreview } from './components/islamic/DesignPreview';
import { AdhkarHome, AdhkarList } from './features/adhkar/Adhkar';
import { ProgressPage } from './features/progress/Progress';
import { registerServiceWorker } from '@/lib/updates';
import { RuqyahPage } from '@/features/adhkar/Ruqyah';
import { SinsPage } from '@/features/adhkar/Sins';
import { DuaChapter, DuasHome, DistressPage } from '@/features/duas/Duas';
import { LecturesFeed } from '@/features/lectures/Lectures';
import { DhikrCounterPage, DhikrList } from '@/features/adhkar/DhikrCounter';
import { SettingsPage } from './features/settings/Settings';

// Auth pulls in better-auth's client + QR code generator — only load it when visited.
const AccountPage = lazy(() => import('./features/auth/Account').then((m) => ({ default: m.AccountPage })));

const queryClient = new QueryClient({ defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false, gcTime: 30 * 60_000 } } });

const router = createBrowserRouter([
  {
    element: <AppShell />,
    children: [
      { path: '/', element: <Home /> },
      { path: '/quran', element: <SurahIndex /> },
      { path: '/quran/page/:page', element: <Reader /> },
      { path: '/quran/:surah', element: <Reader /> },
      { path: '/listen', element: <ListenHome /> },
      { path: '/listen/:id', element: <ReciterPage /> },
      { path: '/prayer', element: <PrayerPage /> },
      { path: '/prayer/stats', element: <PrayerStatsPage /> },
      { path: '/fasting', element: <FastingPage /> },
      { path: '/khatmah', element: <KhatmahList /> },
      { path: '/hifz', element: <HifzHome /> },
      { path: '/hifz/lesson', element: <HifzLesson /> },
      { path: '/hifz/review/:page', element: <HifzReview /> },
      { path: '/hifz/guide', element: <HifzGuide /> },
      { path: '/khatmah/:id', element: <FocusReader /> },
      { path: '/adhkar', element: <AdhkarHome /> },
      { path: '/adhkar/ruqyah', element: <RuqyahPage /> },
      { path: '/adhkar/sins', element: <SinsPage /> },
      { path: '/adhkar/dhikr', element: <DhikrList /> },
      { path: '/adhkar/dhikr/:id', element: <DhikrCounterPage /> },
      { path: '/duas', element: <DuasHome /> },
      { path: '/lectures', element: <LecturesFeed /> },
      { path: '/duas/distress', element: <DistressPage /> },
      { path: '/duas/:id', element: <DuaChapter /> },
      { path: '/adhkar/:when', element: <AdhkarList /> },
      { path: '/progress', element: <ProgressPage /> },
      { path: '/settings', element: <SettingsPage /> },
      { path: '/settings/:section', element: <SettingsPage /> },
      { path: '/account', element: <Suspense fallback={<div className="page-pad p-8 text-center text-muted">Loading…</div>}><AccountPage /></Suspense> },
      ...(import.meta.env.DEV ? [{ path: '/_design', element: <DesignPreview /> }] : []),
      { path: '*', element: <Home /> },
    ],
  },
]);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);

registerServiceWorker();
